import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { messageForError } from '@proctoring/api-client';
import { CANDIDATE_ACCOUNT_WINDOW_HOURS } from '@proctoring/shared';
import { useCandidateProfile } from '../../api/queries.js';
import { FullPageMessage } from '../../components/FullPageMessage.js';
import { StateBadge } from '../../components/StateBadge.js';
import { formatDateTime, localTimeZone } from '../../components/format.js';

export function CandidateProfilePage(): ReactElement {
  const { data, isPending, error } = useCandidateProfile();

  if (isPending) return <FullPageMessage title="Loading your profile…" />;
  if (error) {
    return <FullPageMessage title="Could not load your profile" detail={messageForError(error)} />;
  }

  const { user, upcomingInterview } = data;

  return (
    <div className="mx-auto w-full max-w-3xl px-6 py-10">
      <h1 className="text-xl font-semibold tracking-tight text-session-text">
        Welcome, {user.fullName}
      </h1>
      <p className="mt-1 text-sm text-session-muted">
        Your interview details and the checks to run before the day.
      </p>

      <section className="panel mt-8 p-5" aria-labelledby="account-heading">
        <h2 id="account-heading" className="text-sm font-semibold text-session-text">
          Account
        </h2>
        <dl className="mt-4 grid gap-x-8 gap-y-4 sm:grid-cols-2">
          <div>
            <dt className="field-label">Full name</dt>
            <dd className="text-sm text-session-text">{user.fullName}</dd>
          </div>
          <div>
            <dt className="field-label">Email</dt>
            <dd className="text-sm text-session-text">{user.email}</dd>
          </div>
          <div>
            <dt className="field-label">Timezone</dt>
            <dd className="text-sm text-session-text">{localTimeZone()}</dd>
          </div>
          <div>
            <dt className="field-label">Account status</dt>
            <dd className="text-sm text-session-text capitalize">{user.status}</dd>
          </div>
        </dl>
        <p className="mt-4 text-xs text-session-dim">
          Your account stays active for {CANDIDATE_ACCOUNT_WINDOW_HOURS} hours around the interview,
          then it is locked automatically.
        </p>
      </section>

      <section className="panel mt-6 p-5" aria-labelledby="interview-heading">
        <div className="flex items-start justify-between gap-4">
          <h2 id="interview-heading" className="text-sm font-semibold text-session-text">
            Upcoming interview
          </h2>
          {upcomingInterview ? <StateBadge state={upcomingInterview.state} /> : null}
        </div>

        {upcomingInterview ? (
          <>
            <dl className="mt-4 grid gap-x-8 gap-y-4 sm:grid-cols-2">
              <div>
                <dt className="field-label">Scheduled</dt>
                {/* Rendered in the viewer's own timezone — see components/format.ts. */}
                <dd className="text-sm text-session-text">
                  {formatDateTime(upcomingInterview.scheduledAt)}
                </dd>
              </div>
              <div>
                <dt className="field-label">Role</dt>
                <dd className="text-sm text-session-text">{upcomingInterview.jobProfile.name}</dd>
              </div>
              <div>
                <dt className="field-label">Interviewers</dt>
                <dd className="text-sm text-session-text">
                  {upcomingInterview.interviewers.map((person) => person.fullName).join(', ')}
                </dd>
              </div>
            </dl>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link to={`/candidate/interview/${upcomingInterview.id}`} className="btn-primary">
                Interview details
              </Link>
              <Link to="/candidate/system-check" className="btn-secondary">
                Run system check
              </Link>
            </div>
          </>
        ) : (
          <p className="mt-4 text-sm text-session-muted">
            You have no upcoming interview scheduled. You will receive an email once one is booked.
          </p>
        )}
      </section>
    </div>
  );
}
